import { defineStore } from "pinia";
import { dataService, getIndexedId } from "@thegetty-private/rad-app-utils";
import { getArtistMetadata, setMetaValues } from "@/helpers/MetaTagHelper";
import * as configHelper from "@/helpers/configHelper";
import { isGroupTheGetty } from "@/helpers/Group";
import * as localHelpers from "@/helpers/Person";
import * as localLinkedArtHelpers from "@/helpers/LinkedArt";
import * as aat from "@/data/aat";

import {
  transformSearchResultToThumbnailItemWithArtist,
  transformSearchResultToThumbnailItemWithRole
} from "@/helpers/SearchItemHelper";
import { useAatStore } from "@/stores/aat.js";
import { useTgnStore } from "@/stores/tgn.js";

import { getClassifiedAs, normalizeFieldToArray } from "@thegetty/linkedart.js";
import internal from "@/data/internal-ontologies";
import { getLinksFromIdManager, getElasticData } from "./localActionSupport";
import {
  getUlanLink,
  convertToTitleCase,
  getRoleReferences,
  findGettyVocabRecursive
} from "@thegetty-private/linked-data.js";

const TOOK_PLACE_AT = "took_place_at";

let apiUrl = configHelper.getApiPath("/");
let dataUrl = configHelper.getDataUrl("/");
let idManagerBaseUrl = configHelper.getIdManagerDataUrl(dataUrl);

/*
 * allow the overwriting of the app url (testing)
 *
 * @param {string} url
 */
export function setApiPath(url) {
  apiUrl = url;
}

/**
 * allow the overwriting of the data url (testing)
 *
 * @param {string} url
 */
export function setDataUrl(url) {
  dataUrl = url;
  idManagerBaseUrl = configHelper.getIdManagerDataUrl(url);
}

/**
 * ElasticSearch query for related actor works
 *
 * @param {object} filters - the filters to use for the ES search
 *
 * @returns {object} an object containing the params, search results & search total
 */
async function getRelatedWorks(filters) {
  let params = {
    query: [],
    from: 0,
    size: 24,
    filters,
    sort: "title_sort",
    root: apiUrl
  };
  if (!configHelper.includePartsInQueries()) {
    params.is_standalone = true;
  }
  try {
    let response = await dataService.paramsSearch(params);

    if (response?.data?.data?.length > 0) {
      delete params.size;

      // if we don't have deaccessioned values in the facet(s), don't enable it in the search
      if (response?.data?.facets?.include_deaccessioned == undefined) {
        delete params.filters.include_deaccessioned;
      }

      return {
        params,
        results: {
          data: response.data.data,
          total: response.data.total
        }
      };
    }
  } catch (err) {
    console.error(err);
  }

  return undefined;
}

/**
 * ElasticSearch query for related ULAN related actors
 *
 * @param {object} filters - the filters to use for the ES search
 *
 * @returns {object} an object containing the params, search results & search total
 */
async function getRelatedActors(filters) {
  let params = {
    query: [],
    from: 0,
    size: 24,
    root: apiUrl,
    filters: { id: filters },
    path: "api/relatedActors",
    root: apiUrl
  };

  try {
    let response = await dataService.paramsSearch(params);

    if (response?.data?.data?.length > 0) {
      delete params.size;

      return {
        params,
        results: {
          data: response.data.data,
          total: response.data.total
        }
      };
    }
  } catch (err) {
    console.error(err);
  }

  return undefined;
}

async function getActorNationalities(actorLodObject, aatStore, indexedId) {
  let nationalities = [];
  try {
    // prefetching for AAT parts
    let nationalitiesStubs = [];
    normalizeFieldToArray(actorLodObject, "classified_as").forEach((cls) => {
      if (getClassifiedAs(cls, aat.NATIONALITY).length) {
        nationalitiesStubs.push(cls);
      }
    });

    for (let aat of nationalitiesStubs) {
      await addAatValuetoStore(aat, aatStore);
      nationalities.push(aatStore.getAat(aat.id).value);
    }
  } catch (err) {
    console.error(`Error prefetching nationality for ${indexedId}: ` + err);
  }
  return nationalities;
}

async function getActorRoles(actorLodObject, aatStore, indexedId) {
  let roles = [];
  try {
    for (let aat of getRoleReferences(actorLodObject)) {
      await addAatValuetoStore(aat, aatStore);
      roles.push(aatStore.getAat(aat.id).value);
    }
  } catch (err) {
    console.error(`Error prefetching nationality for ${indexedId}: ` + err);
  }
  return roles.length ? roles : [];
}

async function addAatValuetoStore(aat, aatStore) {
  if (aat && aat.id.includes("aat") == false) {
    return;
  }
  await aatStore.addAat(aat.id);
}
/**
 * Gets and builds ULAN data for actor
 *
 * @param {string} ulanId - the ULAN ID for the actor
 * @param {object} aatStore - the AAT store
 * @param {object} tgnStore - the TGN store
 * @param {object} ref - the ref to the store
 */
async function addUlanData(ulanId, aatStore, tgnStore, ref, translate) {
  let ulanUrl = dataService.constructVocabUrl(ulanId.split("/").pop(), "ULAN");
  try {
    const response = await dataService.axiosGet(ulanUrl);
    if (response?.data) {
      // use the ULAN lod to construct the ULAN data for the store
      const ulanLod = response.data;

      // build the ulan data ref
      let ulan = {
        id: ulanId,
        link: ulanId.replace("vocab.getty.edu/", "vocab.getty.edu/page/")
      };

      let nationalities = [];
      let ulanNationalities = localHelpers.getNationalityReferences(ulanLod);

      // add ulanNationalities to AAT store
      for (let nationality of ulanNationalities) {
        await addAatValuetoStore(nationality, aatStore);
        nationalities.push(aatStore.getAat(nationality.id).value);
      }
      // add TGN data to store
      const tgnParts = findGettyVocabRecursive(ulanLod, "tgn");
      let tgnLocations = {};

      for (let tgn of tgnParts) {
        await tgnStore.addTgn(tgn);
        tgnLocations[tgn] = tgnStore.getTgn(tgn);
      }
      // related actors
      let relatedActorIds = localHelpers.getUlanRelatedActors(ulanLod);
      if (relatedActorIds.length) {
        let relatedActors = await getRelatedActors(relatedActorIds);
        if (relatedActors?.results?.data) {
          let transformedActors = relatedActors.results.data.map((actor) =>
            localHelpers.transformRelatedActorResult(actor)
          );
          ulan.relatedActors = {
            data: transformedActors,
            total: relatedActors.results.total
          };
        }
      }
      // other ulan values
      ulan.ulanDescription = localHelpers.getUlanDescription(ulanLod);
      ulan.ulanBorn = tgnPlaceNameFromDate(ulanLod, tgnLocations);
      ulan.ulanDied = tgnPlaceNameFromDate(ulanLod, tgnLocations, "died");
      ulan.ulanNationality = nationalities;
      ulan.ulanDates = localHelpers.getUlanLifeDates(ulanLod, translate);
      ulan.ulanNames = localHelpers.getUlanNames({
        ulanLod: ulanLod,
        langPref: "en",
        primaryName: ref.pageTitle,
        limit: 3
      });
      // filter out undefined ulan data
      Object.keys(ulan).forEach(
        (key) => ulan[key] === undefined && delete ulan[key]
      );
      ref.ulan = ulan;
    }
  } catch (err) {
    console.error(`Error fetching ULAN data for ${ulanUrl}: ` + err);
  }
}
/**
 * Gets a TGN Location Name from a ULAN life date (born/died)
 *
 * @param {ulanLod} object - the ULAN LOD
 * @param {tgnLocations} object - tgnLocations with id as key
 * @param {date} string - born or died
 *
 * @returns {string | undefined } TGN location name string
 */
export function tgnPlaceNameFromDate(ulanLod, tgnLocations, date = "born") {
  let lifeDate = normalizeFieldToArray(ulanLod[date], TOOK_PLACE_AT);
  return lifeDate.length ? tgnLocations[lifeDate[0].id].fullName : undefined;
}
/**
 * Builds the metadata for an Actor using JSON-LD and ULAN
 *
 * @param {object} object - the JSON-LD Actor
 * @param {object} object - ULAN data ref
 * @param {array} nationality - nationality of the person
 * @param {array} roles - array of string role(s) of the person
 *
 * @returns {object} The person metadata
 */
export function getMetadata(object, ulan, nationality = [], roles = []) {
  if (object == undefined) {
    return {};
  }
  let combinedMetadata = {};

  // build metadata from LOD and ULAN LOD
  let dates = localHelpers.getDates(object);
  dates
    ? (combinedMetadata.dates = dates)
    : (combinedMetadata.ulanDates = ulan?.ulanDates);

  combinedMetadata.roles = roles && roles.length > 0 ? roles : undefined;

  let nat = nationality && nationality.length > 0 ? nationality : undefined;
  nat
    ? (combinedMetadata.nationality = nat)
    : (combinedMetadata.ulanNationality = ulan?.ulanNationality);

  let born = localHelpers.getBornPlace(object);
  born
    ? (combinedMetadata.born = born)
    : (combinedMetadata.ulanBorn = ulan?.ulanBorn);

  let died = localHelpers.getDiedPlace(object);
  died
    ? (combinedMetadata.died = died)
    : (combinedMetadata.ulanDied = ulan?.ulanDied);

  // only add names if we have ULAN data
  if (ulan) {
    combinedMetadata.ulanNames = ulan.ulanNames;
  }

  // Filter out undefined values
  Object.keys(combinedMetadata).forEach(
    (key) => combinedMetadata[key] === undefined && delete combinedMetadata[key]
  );

  return combinedMetadata;
}

export const useActorStore = defineStore("actor", {
  state: () => ({
    items: {}
  }),
  actions: {
    async addActor(indexedId, translate) {
      // does actor exist in the store already?
      if (this.items[indexedId]) {
        return;
      }

      let actorLodObject;
      try {
        actorLodObject = await dataService.getAllById(indexedId, dataUrl);
        this.items[indexedId] = { id: indexedId };
      } catch (err) {
        console.error(err);
        return;
      }

      const ref = this.items[indexedId];
      // get elasticsearch data
      let elasticData = await getElasticData(indexedId, apiUrl);

      try {
        if (elasticData) {
          ref.head = {
            title: elasticData.primary_name,
            metadata: setMetaValues(getArtistMetadata(elasticData, translate))
          };
        }
      } catch (e) {
        console.error(e);
      }

      // take actor LOD object & create transformed data fields for store - push results to state as we get them
      // description
      ref.description = localHelpers.getDescription(
        actorLodObject,
        "text/markdown"
      );

      // page title - used for metatags & sticky header
      ref.pageTitle = localHelpers.getPageTitle(actorLodObject);

      ref.type = actorLodObject.type;

      let isGetty = false;
      if (indexedId.indexOf("group") == 0) {
        isGetty = isGroupTheGetty(actorLodObject);
      }
      ref.isGetty = isGetty;
      // sticky header subheading
      ref.subheading = localHelpers.getNationalityAndDates(actorLodObject);

      // access to AAT/TGN store
      const aatStore = useAatStore();
      const tgnStore = useTgnStore();

      let nationalities = await getActorNationalities(
        actorLodObject,
        aatStore,
        indexedId
      );
      let roles = await getActorRoles(actorLodObject, aatStore, indexedId);

      // add ulan data to actor store ref and tgn/aat stores
      const ulanId = getUlanLink(actorLodObject);
      if (ulanId && configHelper.displayUlanMetadata()) {
        await addUlanData(ulanId, aatStore, tgnStore, ref, translate);
      }

      // build metadata with LOD and ULAN data
      let metadata = getMetadata(
        actorLodObject,
        ref.ulan,
        nationalities.map((nationality) => convertToTitleCase(nationality)),
        roles.map((role) => convertToTitleCase(role))
      );

      // make sure not to pass empty metadata object
      if (Object.keys(metadata).length != 0) {
        ref.metadata = metadata;
      }

      // technical metadata
      ref.technicalMetadata = localLinkedArtHelpers.getTechnicalMetadataFields(
        actorLodObject,
        translate
      );

      // related resources links
      // FIXME: only returning ULAN link for now; the ID Manager links are handled differently/elsewhere
      // consider consolidating or refactoring what we refer to as "related resources"
      let relatedResourcesLinks = localHelpers.getRelatedResourcesLinks(
        actorLodObject,
        translate
      );
      ref.relatedResourcesLinks = relatedResourcesLinks;

      let relatedWorksConfig = {
        related_producer_ids: [indexedId],
        include_deaccessioned: [true]
      };

      // created works search results
      let createdWorks = await getRelatedWorks(relatedWorksConfig);

      if (createdWorks) {
        // transform search items to include role to be consumed by RelatedSearchResults
        let transformedItems = createdWorks.results.data.map((item) => {
          let transformed = transformSearchResultToThumbnailItemWithRole(
            item,
            translate,
            { constituentIndexedId: indexedId }
          );
          // hack for the current world where the RelatedSearchResults component can transform records too
          transformed.isTransformed = true;
          return transformed;
        });

        createdWorks.results.data = transformedItems;

        ref.createdWorks = createdWorks;
      }

      let ownedWorksConfig = {
        "provenance.owner.id": [indexedId],
        include_deaccessioned: [true]
      };

      // owned works search results
      let ownedWorks = await getRelatedWorks(ownedWorksConfig);

      if (ownedWorks) {
        // transform search items to be consumed by RelatedSearchResults

        let transformedItems = ownedWorks.results.data.map((item) => {
          let transformed = transformSearchResultToThumbnailItemWithArtist(
            item,
            false, // showCaption
            translate
          );
          // hack for the current world where the RelatedSearchResults component can transform records too
          transformed.isTransformed = true;
          return transformed;
        });

        ownedWorks.results.data = transformedItems;

        ref.ownedWorks = ownedWorks;
      }

      // idManager searches
      const body_id = idManagerBaseUrl + indexedId;
      const typeMap = await getLinksFromIdManager([
        {
          motivation: internal.ID_MANAGER_RELATED_TO_MOTIVATION,
          body_generator: internal.ID_MANAGER_TARGET_GENERATOR,
          body_id: body_id
        },
        {
          motivation: internal.ID_MANAGER_RELATED_CONTENT_MOTIVATION,
          body_generator: internal.ID_MANAGER_TARGET_GENERATOR,
          body_id: body_id
        }
      ]);

      // related video
      if (typeMap[internal.ID_MANAGER_RELATED_CONTENT_GENERATOR]) {
        let relatedVideos = [];
        typeMap[internal.ID_MANAGER_RELATED_CONTENT_GENERATOR].forEach(
          (video) => {
            if (video.id.includes("youtu")) {
              relatedVideos.push({
                id: video.id,
                heading: video.title,
                linkProps: {
                  href: video.id,
                  withIcon: "after",
                  iconName: "outbound"
                },
                linkText: translate("actor_page.related_video_link", {
                  source: "YouTube"
                })
              });
            }
          }
        );
        ref.relatedVideos = relatedVideos;
      }

      // related exhibitions
      // first grab related / equivalent exhibition results through ES
      let transformedExhibitions = [];
      let relatedExhibitionsUrl = apiUrl + "api/relatedExhibitions";
      const response = await dataService.axiosGet(
        relatedExhibitionsUrl,
        undefined,
        {
          id: indexedId
        }
      );

      if (response && response?.data?.relatedExhibitions?.length) {
        transformedExhibitions =
          localHelpers.transformRelatedExhibitionsResults(
            response.data.relatedExhibitions
          );
      }

      // next check for id manager results for related exhibitions
      // let idManagerIds;
      if (typeMap[internal.ID_MANAGER_TARGET_GENERATOR]) {
        // get related exhibition details through sitewide search results
        // array of exhibition indexedIds from id manager related search
        // idManagerIds = typeMap[internal.ID_MANAGER_TARGET_GENERATOR].map(
        //   (exhibition) => getIndexedId(exhibition.id)
        // );

        // FIXME: refactor to use sitewide search logic instead eventually
        try {
          const slugs = await dataService.getSlugs(
            typeMap[internal.ID_MANAGER_TARGET_GENERATOR],
            idManagerBaseUrl,
            internal.ID_MANAGER_SOURCE_GENERATOR,
            internal.ID_MANAGER_TARGET_GENERATOR
          );

          typeMap[internal.ID_MANAGER_TARGET_GENERATOR].forEach((item) => {
            const exhibitionIndexedId = getIndexedId(item.id);
            const slugWithPath = slugs[exhibitionIndexedId];
            transformedExhibitions.push({
              id: exhibitionIndexedId,

              id: item.id,
              // fixme: with the SitewideSearch lookup this shouldn't be here
              heading: item.title.replace("Related exhibition: ", ""),
              // fixme: add dates
              isGridItemClickable: true,
              linkProps: {
                to: {
                  name: slugWithPath.path,
                  state: {
                    indexedId: exhibitionIndexedId,
                    slug: slugWithPath.slug
                  },
                  params: {
                    slug: slugWithPath.slug
                  }
                }
              }
            });
          });
        } catch (e) {
          console.error(e);
        }
      }
      if (transformedExhibitions.length) {
        ref.relatedExhibitions = transformedExhibitions;
      }
    }
  },
  getters: {
    // NOTE: getters do not accept parameters, but can return a function that receives one
    /**
     * Gets the transformed actor data object from the store
     * @param {object} state - automatic access to state
     * @return {object}
     * @example
     *  {
     *    description: "",
     *    id: "type/1234",
     *    isGetty: false,
     *    metadata: {...},
     *    pageTitle: "Vincent van Gogh",
     *    relatedResourcesLinks: [],
     *    subheading: "Dutch, 1853 - 1890",
     *    technicalMetadata: {...},
     *    type: "Person"
     *  }
     */
    getActor(state) {
      /**
       * [returned function of getter - returns a function that has access to state & can receive a paramater]
       * @param {string} indexedId - indexedId of actor to retrieve from store
       */
      return (indexedId) => {
        if (state.items[indexedId]) {
          return state.items[indexedId];
        } else {
          return undefined;
        }
      };
    }
  }
});
